// =============================================================================
// BP-10-04: Case Timeline Component
// BrightPath Platform - Event Timeline Display for Legal Cases
// =============================================================================

import React, { useState } from 'react';
import {
  FileText,
  Scale,
  Users,
  UserCheck,
  Gavel,
  File,
  StickyNote,
  Flag,
  Calendar,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Check,
  MoreHorizontal,
  Edit,
  Trash2,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { CaseEvent, CaseEventTypeValue, EventPriorityType } from '../types/attorney';
import { EVENT_TYPE_LABELS } from '../types/attorney';
import { formatDate, formatRelativeDate } from '@/lib/formatters';

// =============================================================================
// Event Type Configuration
// =============================================================================

const eventTypeConfig: Record<
  CaseEventTypeValue,
  { icon: React.ElementType; color: string; bgColor: string }
> = {
  filing: { icon: FileText, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  hearing: { icon: Scale, color: 'text-purple-600', bgColor: 'bg-purple-100' },
  deposition: { icon: Users, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  mediation: { icon: UserCheck, color: 'text-teal-600', bgColor: 'bg-teal-100' },
  motion: { icon: Gavel, color: 'text-red-600', bgColor: 'bg-red-100' },
  document: { icon: File, color: 'text-gray-600', bgColor: 'bg-gray-100' },
  note: { icon: StickyNote, color: 'text-amber-600', bgColor: 'bg-amber-100' },
  milestone: { icon: Flag, color: 'text-sage-600', bgColor: 'bg-sage-100' },
};

const priorityConfig: Record<EventPriorityType, { color: string; label: string }> = {
  high: { color: 'bg-red-500', label: 'High Priority' },
  medium: { color: 'bg-amber-500', label: 'Medium Priority' },
  low: { color: 'bg-gray-400', label: 'Low Priority' },
};

// =============================================================================
// EventCard Component
// =============================================================================

interface EventCardProps {
  event: CaseEvent;
  onEdit?: (event: CaseEvent) => void;
  onDelete?: (event: CaseEvent) => void;
  onComplete?: (event: CaseEvent) => void;
  className?: string;
}

export function EventCard({
  event,
  onEdit,
  onDelete,
  onComplete,
  className,
}: EventCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  
  const config = eventTypeConfig[event.eventType];
  const Icon = config.icon;
  const isCompleted = !!event.completedAt;
  const isPastDue = event.dueDate && new Date(event.dueDate) < new Date() && !isCompleted;
  
  return (
    <div
      className={cn(
        'relative bg-white rounded-lg border transition-all duration-200',
        isCompleted
          ? 'border-sage/30 bg-sage-50/30'
          : isPastDue
            ? 'border-red-200 bg-red-50/30'
            : 'border-warm-gray/10 hover:border-sage/30',
        className
      )}
    >
      {/* Priority Indicator */}
      <div
        className={cn(
          'absolute left-0 top-0 bottom-0 w-1 rounded-l-lg',
          priorityConfig[event.priority].color
        )}
      />
      
      {/* Main Content */}
      <div className="p-4 pl-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          {/* Event Type Icon */}
          <div
            className={cn(
              'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
              config.bgColor
            )}
          >
            <Icon className={cn('w-5 h-5', config.color)} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4
                className={cn(
                  'font-medium text-navy',
                  isCompleted && 'line-through text-warm-gray'
                )}
              >
                {event.title}
              </h4>
              {event.visibleToClient ? (
                <Eye className="w-4 h-4 text-sage" title="Visible to client" />
              ) : (
                <EyeOff className="w-4 h-4 text-warm-gray/50" title="Not visible to client" />
              )}
            </div>
            
            <div className="flex items-center gap-3 text-sm text-warm-gray">
              <span className="px-2 py-0.5 bg-cream rounded text-xs font-medium">
                {EVENT_TYPE_LABELS[event.eventType]}
              </span>
              {event.eventDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(event.eventDate)}
                </span>
              )}
              {event.dueDate && !isCompleted && (
                <span
                  className={cn(
                    'flex items-center gap-1',
                    isPastDue && 'text-red-600 font-medium'
                  )}
                >
                  Due: {formatDate(event.dueDate)}
                </span>
              )}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            {isCompleted && (
              <span className="flex items-center gap-1 text-xs text-sage font-medium">
                <Check className="w-4 h-4" />
                Completed
              </span>
            )}
            
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 rounded hover:bg-cream transition-colors"
              >
                <MoreHorizontal className="w-5 h-5 text-warm-gray" />
              </button>
              
              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-medium border border-warm-gray/10 py-1 z-20">
                    {onEdit && (
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onEdit(event);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-navy hover:bg-cream flex items-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                    {onComplete && !isCompleted && (
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onComplete(event);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-sage hover:bg-cream flex items-center gap-2"
                      >
                        <Check className="w-4 h-4" />
                        Mark Complete
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onDelete(event);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Description (expandable) */}
        {event.description && (
          <div className="mt-3 ml-13">
            <p
              className={cn(
                'text-sm text-warm-gray',
                !isExpanded && 'line-clamp-2'
              )}
            >
              {event.description}
            </p>
            {event.description.length > 150 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-xs text-sage hover:text-sage-dark flex items-center gap-1 mt-1"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" /> Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" /> Show more
                  </>
                )}
              </button>
            )}
          </div>
        )}
        
        {/* Attachments */}
        {event.attachments && event.attachments.length > 0 && (
          <div className="mt-3 ml-13 flex flex-wrap gap-2">
            {event.attachments.map((attachment) => (
              <a
                key={attachment.id}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2 py-1 bg-cream rounded text-xs text-navy hover:bg-cream-dark transition-colors"
              >
                <File className="w-3 h-3" />
                {attachment.filename}
              </a>
            ))}
          </div>
        )}
        
        {/* Footer Meta */}
        <div className="mt-3 ml-13 flex items-center gap-3 text-xs text-warm-gray/70">
          {event.createdBy && (
            <span>
              Added by {event.createdBy.firstName} {event.createdBy.lastName}
            </span>
          )}
          <span>•</span>
          <span>{formatRelativeDate(event.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// CaseTimeline Component
// =============================================================================

interface CaseTimelineProps {
  events: CaseEvent[];
  onAddEvent?: () => void;
  onEditEvent?: (event: CaseEvent) => void;
  onDeleteEvent?: (event: CaseEvent) => void;
  onCompleteEvent?: (event: CaseEvent) => void;
  showClientVisibleOnly?: boolean;
  className?: string;
}

export function CaseTimeline({
  events,
  onAddEvent,
  onEditEvent,
  onDeleteEvent,
  onCompleteEvent,
  showClientVisibleOnly = false,
  className,
}: CaseTimelineProps) {
  // Filter events if needed
  const filteredEvents = showClientVisibleOnly
    ? events.filter((e) => e.visibleToClient)
    : events;
  
  // Group events by month
  const groupedEvents = filteredEvents.reduce<Record<string, CaseEvent[]>>(
    (acc, event) => {
      const date = event.eventDate || event.createdAt;
      const monthKey = new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
      });
      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }
      acc[monthKey].push(event);
      return acc;
    },
    {}
  );
  
  // Sort groups by date (most recent first)
  const sortedMonths = Object.keys(groupedEvents).sort((a, b) => {
    return new Date(b).getTime() - new Date(a).getTime();
  });
  
  if (filteredEvents.length === 0) {
    return (
      <div className={cn('text-center py-12', className)}>
        <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-8 h-8 text-warm-gray" />
        </div>
        <h3 className="font-medium text-navy mb-1">No events yet</h3>
        <p className="text-sm text-warm-gray mb-4">
          Start building the case timeline by adding events
        </p>
        {onAddEvent && (
          <button
            onClick={onAddEvent}
            className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
          >
            Add First Event
          </button>
        )}
      </div>
    );
  }
  
  return (
    <div className={cn('space-y-8', className)}>
      {sortedMonths.map((month) => (
        <div key={month}>
          {/* Month Header */}
          <div className="flex items-center gap-3 mb-4">
            <h3 className="font-display font-semibold text-navy">{month}</h3>
            <div className="flex-1 h-px bg-warm-gray/20" />
            <span className="text-sm text-warm-gray">
              {groupedEvents[month].length} event
              {groupedEvents[month].length !== 1 ? 's' : ''}
            </span>
          </div>
          
          {/* Events */}
          <div className="relative pl-6 space-y-4">
            {/* Timeline Line */}
            <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-warm-gray/20" />
            
            {groupedEvents[month]
              .sort((a, b) => {
                const dateA = new Date(a.eventDate || a.createdAt);
                const dateB = new Date(b.eventDate || b.createdAt);
                return dateB.getTime() - dateA.getTime();
              })
              .map((event) => (
                <div key={event.id} className="relative">
                  {/* Timeline Dot */}
                  <div
                    className={cn(
                      'absolute -left-6 top-5 w-4 h-4 rounded-full border-2 border-white',
                      event.completedAt
                        ? 'bg-sage'
                        : eventTypeConfig[event.eventType].bgColor
                    )}
                  />
                  
                  <EventCard
                    event={event}
                    onEdit={onEditEvent}
                    onDelete={onDeleteEvent}
                    onComplete={onCompleteEvent}
                  />
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default CaseTimeline;


